import { OrderProductPriceType, ProductStatus } from "@fena/toolkit-types";
import { useMemo } from "react";
import styled from "styled-components";
import {
  NotificationTypes,
  useNotification,
} from "../../../hooks/useNotification";
import { QuestionMark } from "../../../modules/open/components/QuestionMark";
import { useOrderGenerationContext } from "../../../modules/private/order-generation-module/context/OrderGenerationContextProvider";
import {
  getProductPriceRange,
  isDiscountActive,
} from "../../../util/data-utils";
import { ImageIcon } from "../../icons";
import { Typography } from "../typography/Typography";
import { TypographyVariant } from "../typography/Typography.interface";
import { ProductCardProps } from "./ProductCard.interface";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #dbe3eb;
  border-radius: 10px;
  @media (max-width: 576px) {
    background-color: #fff;
  }
`;

const DiscountPlate = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  background-color: #ef6355;
  border-radius: 3px;
  padding: 0 10px;
  p {
    color: white;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 175px;
  border-radius: 9px;
  object-fit: contain;
`;

const GrayBg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 175px;
  border-radius: 5px;
  background-color: transparent;
  svg {
    scale: 4;
  }
`;

const Description = styled.div`
  padding: 10px 10px 20px;
  overflow: hidden;
  word-wrap: break-word;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;
  gap: 3px;
`;

/* const StyledInfoIcon = styled(Info)`
  cursor: pointer;
`; */

const Price = styled(Typography)``;

const OldPrice = styled(Typography)`
  color: #8181a5;
  text-decoration: line-through;
`;

const ImageWrapper = styled.div`
  padding: 10px 10px 0;
`;

export const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  product,
  addToCart,
}) => {
  const { cart, productsPriceType, currentCurrency } =
    useOrderGenerationContext();
  const { showNotification } = useNotification();
  const defaultVariant = product.variants[0];
  const someVariantHasSegmentPrice = product.variants.some(
    (v) => !!v.pricing?.[currentCurrency.currencyCode]?.segmentPricing?.length
  );

  const activeDiscount = isDiscountActive(defaultVariant);

  const variantPrice = useMemo(() => {
    return getProductPriceRange(
      product.variants,
      currentCurrency.currencyCode,
      productsPriceType === OrderProductPriceType.WHOLESALE
    );
  }, [cart, product.variants, productsPriceType, currentCurrency.currencyCode]);

  const handleAddProduct = () => {
    if (product.status === ProductStatus.INACTIVE) {
      showNotification(
        "This product is marked as inactive, and cannot be added to your cart",
        NotificationTypes.WARNING
      );
    } else {
      addToCart();
    }
  };

  return (
    <Wrapper onClick={handleAddProduct}>
      {!!activeDiscount && (
        <DiscountPlate>
          <Typography variant={TypographyVariant.BODY6}>
            {activeDiscount?.discount}% OFF
          </Typography>
        </DiscountPlate>
      )}

      <ImageWrapper>
        {defaultVariant?.images?.length ? (
          <Image src={defaultVariant.images[0].url} alt="product image" />
        ) : (
          <GrayBg>
            <ImageIcon />
          </GrayBg>
        )}
      </ImageWrapper>
      <Description>
        <Typography variant={TypographyVariant.BODY3}>
          {product.name}
        </Typography>
        {product.variants.length > 1 && (
          <Typography
            style={{ textDecoration: "underline", marginTop: 3 }}
            variant={TypographyVariant.BODY7}
          >
            {product.variants.length} variants
          </Typography>
        )}
        <PriceContainer>
          {!!activeDiscount && (
            <OldPrice variant={TypographyVariant.BODY5}>
              {variantPrice}
            </OldPrice>
          )}
          <Price variant={TypographyVariant.BODY5}>{variantPrice}</Price>
          {someVariantHasSegmentPrice && (
            <QuestionMark
              id={product._id}
              tooltipText={"This product has a segment price."}
              customBackgroundColor={"#13273f"}
              customQuestionMarkColorHover={"#13273f"}
            />
          )}
        </PriceContainer>
      </Description>
    </Wrapper>
  );
};
